<template>
  <div>
    <Modal
      v-model="visibleShow"
      :title="modalTitle"
      width="1120"
      class-name="tmall-config-modal"
      footer-hide
      @on-cancel="cancel"
    >
      <div class="tmall-config">
        <Tabs v-model="activeTab">
          <TabPane label="开放平台应用" name="open-app">
            <div class="section-toolbar">
              <span class="section-title">应用列表</span>
              <Button type="primary" icon="md-add" @click="openCreateModal">新增应用</Button>
            </div>
            <Table
              size="small"
              :columns="openAppColumns"
              :data="openApps"
              :loading="openAppLoading"
            />
          </TabPane>
          <TabPane label="天猫 API 配置" name="agent-api">
            <div class="api-form-shell">
              <Spin fix v-if="agentApiLoading" />
              <Form
                ref="agentApiForm"
                :model="agentApiForm"
                :rules="agentApiRules"
                :label-width="120"
              >
                <FormItem label="AppKey:" prop="appkey">
                  <div class="secret-input-row">
                    <Input
                      v-model.trim="agentApiForm.appkey"
                      maxlength="80"
                      placeholder="请输入天猫应用 AppKey"
                    />
                    <Tooltip content="复制 AppKey" placement="top">
                      <Button
                        icon="md-copy"
                        aria-label="复制 AppKey"
                        :disabled="!agentApiForm.appkey"
                        @click="copyText(agentApiForm.appkey)"
                      />
                    </Tooltip>
                  </div>
                </FormItem>
                <FormItem label="AppSecret:" prop="appsecret">
                  <div class="secret-input-row">
                    <Input
                      v-model.trim="agentApiForm.appsecret"
                      :type="showAgentApiSecret ? 'text' : 'password'"
                      maxlength="255"
                      placeholder="请输入天猫应用 AppSecret"
                    />
                    <Tooltip :content="showAgentApiSecret ? '隐藏 AppSecret' : '显示 AppSecret'" placement="top">
                      <Button
                        :icon="showAgentApiSecret ? 'md-eye-off' : 'md-eye'"
                        :aria-label="showAgentApiSecret ? '隐藏 AppSecret' : '显示 AppSecret'"
                        @click="showAgentApiSecret = !showAgentApiSecret"
                      />
                    </Tooltip>
                    <Tooltip content="复制 AppSecret" placement="top">
                      <Button
                        icon="md-copy"
                        aria-label="复制 AppSecret"
                        :disabled="!agentApiForm.appsecret"
                        @click="copyText(agentApiForm.appsecret)"
                      />
                    </Tooltip>
                  </div>
                </FormItem>
                <FormItem label="ISV 标识:" prop="isv">
                  <Input
                    v-model.trim="agentApiForm.isv"
                    maxlength="50"
                    placeholder="请输入 ISV 标识"
                  />
                </FormItem>
                <FormItem>
                  <Button
                    type="primary"
                    icon="md-checkmark"
                    :loading="agentApiSaving"
                    @click="saveAgentApi"
                  >保存配置</Button>
                </FormItem>
              </Form>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </Modal>

    <Modal
      v-model="openAppModal.visible"
      :title="openAppModal.data.id ? '编辑开放平台应用' : '新增开放平台应用'"
      width="640"
      class-name="tmall-open-app-modal"
      :mask-closable="false"
    >
      <Form
        ref="openAppForm"
        :model="openAppModal.data"
        :rules="openAppRules"
        :label-width="110"
      >
        <FormItem label="应用名称:" prop="name">
          <Input
            v-model.trim="openAppModal.data.name"
            maxlength="255"
            placeholder="请输入应用名称"
          />
        </FormItem>
        <FormItem label="App ID:" prop="app_id">
          <Input
            v-model.trim="openAppModal.data.app_id"
            maxlength="32"
            placeholder="请输入 App ID"
          />
        </FormItem>
        <FormItem label="App Key:" v-if="openAppModal.data.id">
          <div class="secret-input-row">
            <Input
              :value="openAppModal.showKey ? openAppModal.data.app_key : maskSecret(openAppModal.data.app_key)"
              readonly
            />
            <Tooltip :content="openAppModal.showKey ? '隐藏 App Key' : '显示 App Key'" placement="top">
              <Button
                :icon="openAppModal.showKey ? 'md-eye-off' : 'md-eye'"
                :aria-label="openAppModal.showKey ? '隐藏 App Key' : '显示 App Key'"
                @click="openAppModal.showKey = !openAppModal.showKey"
              />
            </Tooltip>
            <Tooltip content="复制 App Key" placement="top">
              <Button
                icon="md-copy"
                aria-label="复制 App Key"
                :disabled="!openAppModal.data.app_key"
                @click="copyText(openAppModal.data.app_key)"
              />
            </Tooltip>
          </div>
        </FormItem>
        <FormItem label="运营到期时间:">
          <DatePicker
            type="datetime"
            format="yyyy-MM-dd HH:mm:ss"
            :value="openAppModal.data.end_time"
            placeholder="请选择运营到期时间"
            style="width: 100%"
            @on-change="changeEndTime"
          />
        </FormItem>
        <FormItem label="状态:" prop="status">
          <Select v-model="openAppModal.data.status" style="width: 180px">
            <Option :value="0">正常</Option>
            <Option :value="1">禁用</Option>
          </Select>
        </FormItem>
        <FormItem label="IP 白名单:">
          <Input
            v-model.trim="openAppModal.data.ip"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 4 }"
            placeholder="请输入允许访问的 IP"
          />
        </FormItem>
      </Form>
      <div slot="footer">
        <Button @click="openAppModal.visible = false">取消</Button>
        <Button type="primary" :loading="openAppModal.saving" @click="saveOpenApp">保存</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  GetTmallOpenAppList,
  GetTmallOpenAppInfo,
  SaveTmallOpenApp,
  ResetTmallOpenAppKey,
  GetTmallAgentApi,
  SaveTmallAgentApi
} from '@/api/tmall'

const emptyOpenApp = () => ({
  id: '',
  app_id: '',
  app_key: '',
  name: '',
  end_time: '',
  status: 0,
  ip: ''
})

const emptyAgentApi = agentId => ({
  id: '',
  agent_id: agentId,
  appkey: '',
  appsecret: '',
  isv: ''
})

export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    agentId: {
      type: [Number, String],
      default: 0
    },
    agentName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      visibleShow: this.visible,
      activeTab: 'open-app',
      openApps: [],
      openAppLoading: false,
      detailLoadingId: '',
      resetKeyLoadingId: '',
      showAppKeys: {},
      agentApiLoading: false,
      agentApiSaving: false,
      showAgentApiSecret: false,
      agentApiForm: emptyAgentApi(Number(this.agentId) || 0),
      openAppModal: {
        visible: false,
        saving: false,
        showKey: false,
        data: emptyOpenApp()
      },
      openAppRules: {
        name: [
          { required: true, message: '请输入应用名称', trigger: 'blur' },
          { max: 255, message: '应用名称不能超过 255 个字符', trigger: 'blur' }
        ],
        app_id: [
          { required: true, message: '请输入 App ID', trigger: 'blur' },
          { max: 32, message: 'App ID 不能超过 32 个字符', trigger: 'blur' }
        ]
      },
      agentApiRules: {
        appkey: [
          { required: true, message: '请输入 AppKey', trigger: 'blur' },
          { max: 80, message: 'AppKey 不能超过 80 个字符', trigger: 'blur' }
        ],
        appsecret: [
          { required: true, message: '请输入 AppSecret', trigger: 'blur' },
          { max: 255, message: 'AppSecret 不能超过 255 个字符', trigger: 'blur' }
        ],
        isv: [
          { required: true, message: '请输入 ISV 标识', trigger: 'blur' },
          { max: 50, message: 'ISV 标识不能超过 50 个字符', trigger: 'blur' }
        ]
      },
      openAppColumns: [
        { title: '应用名称', key: 'name', minWidth: 150, tooltip: true },
        { title: 'App ID', key: 'app_id', minWidth: 130, tooltip: true },
        {
          title: 'App Key',
          key: 'app_key',
          minWidth: 245,
          render: (h, params) => this.renderAppKey(h, params.row)
        },
        {
          title: '运营到期时间',
          key: 'end_time',
          width: 165,
          render: (h, params) => h('span', this.formatTime(params.row.end_time))
        },
        { title: 'IP 白名单', key: 'ip', minWidth: 120, tooltip: true },
        {
          title: '状态',
          key: 'status',
          width: 85,
          render: (h, params) => this.renderStatus(h, params.row.status)
        },
        {
          title: '操作',
          key: 'operator',
          width: 260,
          fixed: 'right',
          render: (h, params) => this.renderActions(h, params.row)
        }
      ]
    }
  },
  computed: {
    currentAgentId () {
      return Number(this.agentId) || 0
    },
    modalTitle () {
      return this.agentName ? `天猫校园配置 · ${this.agentName}` : '天猫校园配置'
    }
  },
  methods: {
    isSuccess (result) {
      return !!result && result.status === true && result.success !== false
    },
    resultMessage (result, fallback) {
      return (result && (result.msg || result.message)) || fallback
    },
    cancel () {
      this.visibleShow = false
      this.openAppModal.visible = false
      this.$emit('cancel')
    },
    loadData () {
      this.activeTab = 'open-app'
      this.showAppKeys = {}
      this.showAgentApiSecret = false
      this.openAppModal.visible = false
      this.getOpenApps()
      this.getAgentApi()
    },
    getOpenApps (revealId) {
      const agentId = this.currentAgentId
      if (!agentId) return
      this.openAppLoading = true
      GetTmallOpenAppList({ agent_id: agentId }).then(res => {
        if (agentId !== this.currentAgentId) return
        this.openAppLoading = false
        const result = res.data
        if (!this.isSuccess(result)) {
          this.$Message.error(this.resultMessage(result, '获取开放平台应用失败'))
          return
        }
        this.openApps = Array.isArray(result.data) ? result.data : []
        if (revealId) this.$set(this.showAppKeys, revealId, true)
      }).catch(() => {
        if (agentId !== this.currentAgentId) return
        this.openAppLoading = false
        this.$Message.error('获取开放平台应用失败')
      })
    },
    getAgentApi () {
      const agentId = this.currentAgentId
      if (!agentId) return
      this.agentApiLoading = true
      GetTmallAgentApi({ agent_id: agentId }).then(res => {
        if (agentId !== this.currentAgentId) return
        this.agentApiLoading = false
        const result = res.data
        if (!this.isSuccess(result)) {
          this.$Message.error(this.resultMessage(result, '获取天猫 API 配置失败'))
          return
        }
        const data = result.data || {}
        this.agentApiForm = {
          id: data.id || '',
          agent_id: agentId,
          appkey: data.appkey || data.app_key || '',
          appsecret: data.appsecret || data.app_secret || '',
          isv: data.isv || ''
        }
      }).catch(() => {
        if (agentId !== this.currentAgentId) return
        this.agentApiLoading = false
        this.$Message.error('获取天猫 API 配置失败')
      })
    },
    openCreateModal () {
      this.openAppModal = {
        visible: true,
        saving: false,
        showKey: false,
        data: emptyOpenApp()
      }
      this.$nextTick(() => {
        if (this.$refs.openAppForm) this.$refs.openAppForm.resetFields()
      })
    },
    editOpenApp (row) {
      const agentId = this.currentAgentId
      this.detailLoadingId = row.id
      GetTmallOpenAppInfo({ agent_id: agentId, id: row.id }).then(res => {
        this.detailLoadingId = ''
        if (agentId !== this.currentAgentId) return
        const result = res.data
        if (!this.isSuccess(result)) {
          this.$Message.error(this.resultMessage(result, '获取开放平台应用详情失败'))
          return
        }
        const data = result.data || {}
        this.openAppModal = {
          visible: true,
          saving: false,
          showKey: false,
          data: {
            id: data.id,
            app_id: data.app_id || '',
            app_key: data.app_key || '',
            name: data.name || '',
            end_time: data.end_time ? this.formatTime(data.end_time, '') : '',
            status: Number(data.status) === 1 ? 1 : 0,
            ip: data.ip || ''
          }
        }
      }).catch(() => {
        this.detailLoadingId = ''
        this.$Message.error('获取开放平台应用详情失败')
      })
    },
    saveOpenApp () {
      this.$refs.openAppForm.validate(valid => {
        if (!valid) return
        const data = this.openAppModal.data
        const payload = {
          agent_id: this.currentAgentId,
          app_id: data.app_id,
          name: data.name,
          end_time: data.end_time || null,
          status: Number(data.status),
          ip: data.ip || ''
        }
        if (data.id) payload.id = data.id
        this.openAppModal.saving = true
        SaveTmallOpenApp(payload).then(res => {
          this.openAppModal.saving = false
          const result = res.data
          if (!this.isSuccess(result)) {
            this.$Message.error(this.resultMessage(result, '保存开放平台应用失败'))
            return
          }
          const saved = result.data || {}
          this.$Message.success(this.resultMessage(result, '保存成功'))
          this.openAppModal.visible = false
          this.getOpenApps(data.id ? '' : saved.id)
        }).catch(() => {
          this.openAppModal.saving = false
          this.$Message.error('保存开放平台应用失败')
        })
      })
    },
    resetOpenAppKey (row) {
      this.$Modal.confirm({
        title: '重置 App Key',
        content: `确认重置“${row.name}”的 App Key？旧密钥将立即失效。`,
        onOk: () => {
          this.resetKeyLoadingId = row.id
          ResetTmallOpenAppKey({ agent_id: this.currentAgentId, id: row.id }).then(res => {
            this.resetKeyLoadingId = ''
            const result = res.data
            if (!this.isSuccess(result)) {
              this.$Message.error(this.resultMessage(result, '重置 App Key 失败'))
              return
            }
            const target = this.openApps.find(item => Number(item.id) === Number(row.id))
            if (target && result.data) target.app_key = result.data.app_key
            this.$set(this.showAppKeys, row.id, true)
            this.$Message.success('App Key 已重置，请及时复制新密钥')
          }).catch(() => {
            this.resetKeyLoadingId = ''
            this.$Message.error('重置 App Key 失败')
          })
        }
      })
    },
    deleteOpenApp (row) {
      this.$Modal.confirm({
        title: '删除开放平台应用',
        content: `确认删除“${row.name}”？`,
        onOk: () => {
          SaveTmallOpenApp({
            agent_id: this.currentAgentId,
            id: row.id,
            status: 2
          }).then(res => {
            const result = res.data
            if (!this.isSuccess(result)) {
              this.$Message.error(this.resultMessage(result, '删除开放平台应用失败'))
              return
            }
            this.$Message.success(this.resultMessage(result, '删除成功'))
            this.getOpenApps()
          }).catch(() => {
            this.$Message.error('删除开放平台应用失败')
          })
        }
      })
    },
    saveAgentApi () {
      this.$refs.agentApiForm.validate(valid => {
        if (!valid) return
        this.agentApiSaving = true
        SaveTmallAgentApi({
          agent_id: this.currentAgentId,
          appkey: this.agentApiForm.appkey,
          appsecret: this.agentApiForm.appsecret,
          isv: this.agentApiForm.isv
        }).then(res => {
          this.agentApiSaving = false
          const result = res.data
          if (!this.isSuccess(result)) {
            this.$Message.error(this.resultMessage(result, '保存天猫 API 配置失败'))
            return
          }
          const data = result.data || {}
          this.agentApiForm = {
            id: data.id || this.agentApiForm.id,
            agent_id: this.currentAgentId,
            appkey: data.appkey || this.agentApiForm.appkey,
            appsecret: data.appsecret || this.agentApiForm.appsecret,
            isv: data.isv || this.agentApiForm.isv
          }
          this.$Message.success(this.resultMessage(result, '保存成功'))
        }).catch(() => {
          this.agentApiSaving = false
          this.$Message.error('保存天猫 API 配置失败')
        })
      })
    },
    renderAppKey (h, row) {
      const visible = !!this.showAppKeys[row.id]
      const value = row.app_key || ''
      return h('div', { class: 'secret-cell' }, [
        h('code', { class: 'secret-value' }, visible ? (value || '-') : this.maskSecret(value)),
        h('Button', {
          props: { type: 'text', size: 'small', icon: visible ? 'md-eye-off' : 'md-eye' },
          attrs: { title: visible ? '隐藏 App Key' : '显示 App Key', 'aria-label': visible ? '隐藏 App Key' : '显示 App Key' },
          on: { click: () => this.$set(this.showAppKeys, row.id, !visible) }
        }),
        h('Button', {
          props: { type: 'text', size: 'small', icon: 'md-copy', disabled: !value },
          attrs: { title: '复制 App Key', 'aria-label': '复制 App Key' },
          on: { click: () => this.copyText(value) }
        })
      ])
    },
    renderStatus (h, status) {
      const statusMap = {
        0: { text: '正常', color: 'success' },
        1: { text: '禁用', color: 'warning' },
        2: { text: '已删除', color: 'error' }
      }
      const current = statusMap[Number(status)] || { text: '未知', color: 'default' }
      return h('Tag', { props: { color: current.color } }, current.text)
    },
    renderActions (h, row) {
      if (Number(row.status) === 2) return h('span', '-')
      return h('div', { class: 'action-buttons' }, [
        h('Button', {
          props: {
            type: 'warning',
            size: 'small',
            icon: 'md-create',
            loading: Number(this.detailLoadingId) === Number(row.id)
          },
          on: { click: () => this.editOpenApp(row) }
        }, '编辑'),
        h('Button', {
          props: {
            size: 'small',
            icon: 'md-refresh',
            loading: Number(this.resetKeyLoadingId) === Number(row.id)
          },
          on: { click: () => this.resetOpenAppKey(row) }
        }, '重置密钥'),
        h('Button', {
          props: { type: 'error', size: 'small', icon: 'md-trash' },
          on: { click: () => this.deleteOpenApp(row) }
        }, '删除')
      ])
    },
    changeEndTime (value) {
      this.openAppModal.data.end_time = value || ''
    },
    formatTime (value, fallback = '-') {
      if (!value) return fallback
      const time = this.$moment(value)
      return time.isValid() ? time.format('YYYY-MM-DD HH:mm:ss') : fallback
    },
    maskSecret (value) {
      if (!value) return '-'
      if (value.length <= 8) return '********'
      return `${value.slice(0, 4)}********${value.slice(-4)}`
    },
    copyText (value) {
      if (!value) return
      const success = () => this.$Message.success('复制成功')
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(value).then(success).catch(() => this.fallbackCopy(value))
        return
      }
      this.fallbackCopy(value)
    },
    fallbackCopy (value) {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (copied) this.$Message.success('复制成功')
      else this.$Message.error('复制失败，请手动复制')
    }
  },
  watch: {
    visible (value) {
      this.visibleShow = value
      if (value && this.currentAgentId) this.loadData()
    }
  }
}
</script>

<style lang="less">
.tmall-config-modal {
  .ivu-modal {
    width: calc(100vw - 32px) !important;
    max-width: 1120px;
  }
  .ivu-modal-body {
    max-height: 78vh;
    overflow-y: auto;
  }
}

.tmall-open-app-modal {
  .ivu-modal {
    width: calc(100vw - 32px) !important;
    max-width: 640px;
  }
}

.tmall-config {
  min-height: 420px;

  .section-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    margin-bottom: 12px;
  }

  .section-title {
    color: #17233d;
    font-size: 16px;
    font-weight: 600;
  }

  .api-form-shell {
    position: relative;
    max-width: 760px;
    min-height: 300px;
    padding-top: 24px;
  }
}

.secret-input-row {
  display: flex;
  align-items: stretch;
  gap: 8px;

  > .ivu-input-wrapper {
    flex: 1;
  }
}

.secret-cell {
  display: flex;
  align-items: center;
  min-height: 32px;
  white-space: nowrap;

  .secret-value {
    display: inline-block;
    width: 142px;
    overflow: hidden;
    color: #515a6e;
    font-family: Menlo, Monaco, Consolas, monospace;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
}

@media (max-width: 640px) {
  .tmall-config {
    min-height: 360px;
  }

  .secret-input-row {
    flex-wrap: wrap;
  }
}
</style>
